'use client';

import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { interactionResponse } from '@zillow/yield-callback';
import { useTranslations } from 'next-intl';
import type { HpSearchScopeDto } from '@zg-rentals/ts-hotpads-api-client';

import { useFilters } from '@/src/lib/filters';
import { useCurrentUser, useDeleteSavedSearch, useSavedSearches } from '@/src/lib/hooks/query';
import {
  buildSavedSearchMatchKey,
  findMatchingSavedSearch,
  getSavedSearchId,
  type SavedSearchAreaBounds,
} from '@/src/lib/savedSearches';
import { SaveSearchModal } from '@/src/components/ListingSearch/SaveSearchModal';
import { emitTrackCreateSearchAlertEvent, emitTrackOpenSearchAlertModalEvent } from '@/src/analytics/hotpads/events';

const PERSISTED_SAVED_SEARCH_KEY = 'hp:saved-search-match-key';

function readPersistedSavedSearchKey(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.sessionStorage.getItem(PERSISTED_SAVED_SEARCH_KEY);
}

function persistSavedSearchKey(key: string) {
  if (typeof window === 'undefined') {
    return;
  }

  window.sessionStorage.setItem(PERSISTED_SAVED_SEARCH_KEY, key);
}

function clearPersistedSavedSearchKey(key?: string) {
  if (typeof window === 'undefined') {
    return;
  }

  const currentKey = window.sessionStorage.getItem(PERSISTED_SAVED_SEARCH_KEY);
  if (key && currentKey !== key) {
    return;
  }

  window.sessionStorage.removeItem(PERSISTED_SAVED_SEARCH_KEY);
}

type SrpSaveSearchContextValue = {
  isSaved: boolean;
  isDisabled: boolean;
  label: string;
  handleToggle: () => void;
};

type SrpSaveSearchProviderProps = {
  children: ReactNode;
  resourceId: string;
  areaId: string;
  searchSlug: string;
  locationText: string;
  areaBounds: SavedSearchAreaBounds;
  /** The area's typed Zillow id; matches saved searches by typed id and feeds the create payload. */
  searchScope?: HpSearchScopeDto;
};

const SrpSaveSearchContext = createContext<SrpSaveSearchContextValue | null>(null);

export function SrpSaveSearchProvider({
  children,
  resourceId,
  areaId,
  searchSlug,
  locationText,
  areaBounds,
  searchScope,
}: SrpSaveSearchProviderProps) {
  const t = useTranslations('search');
  const { filters } = useFilters();
  const { data: currentUser } = useCurrentUser();
  const hasResolvedCurrentUser = currentUser !== undefined;
  const isLoggedIn = currentUser?.loggedIn ?? false;
  const savedSearchesQuery = useSavedSearches({ enabled: isLoggedIn });
  const deleteSavedSearch = useDeleteSavedSearch();
  const savedSearchMatchKey = useMemo(
    () =>
      buildSavedSearchMatchKey({
        resourceId,
        areaId,
        searchSlug,
        filters,
      }),
    [areaId, filters, resourceId, searchSlug],
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [optimisticSaved, setOptimisticSaved] = useState(false);
  const [pendingRemovalSearchId, setPendingRemovalSearchId] = useState<string | null>(null);

  const matchingSearch = useMemo(
    () =>
      findMatchingSavedSearch(savedSearchesQuery.data, {
        resourceId,
        areaId,
        searchSlug,
        filters,
        searchScope,
      }),
    [areaId, filters, resourceId, savedSearchesQuery.data, searchScope, searchSlug],
  );

  const matchingSearchId = matchingSearch ? getSavedSearchId(matchingSearch) : null;
  const isPendingRemoval = !!matchingSearchId && matchingSearchId === pendingRemovalSearchId;

  const openSaveSearchModal = useCallback(() => {
    emitTrackOpenSearchAlertModalEvent();
    setIsModalOpen(true);
  }, []);

  useEffect(() => {
    if (pendingRemovalSearchId && pendingRemovalSearchId !== matchingSearchId) {
      setPendingRemovalSearchId(null);
    }
  }, [matchingSearchId, pendingRemovalSearchId]);

  useEffect(() => {
    const persistedKey = readPersistedSavedSearchKey();
    if (persistedKey === savedSearchMatchKey) {
      setOptimisticSaved(true);
      return;
    }

    if (!matchingSearch && hasResolvedCurrentUser) {
      setOptimisticSaved(false);
    }
  }, [hasResolvedCurrentUser, matchingSearch, savedSearchMatchKey]);

  useEffect(() => {
    if (matchingSearch) {
      persistSavedSearchKey(savedSearchMatchKey);
      setOptimisticSaved(true);
      return;
    }

    if (hasResolvedCurrentUser && !isLoggedIn) {
      // Preserve optimistic state if the user saved this search while unauthenticated
      if (readPersistedSavedSearchKey() !== savedSearchMatchKey) {
        setOptimisticSaved(false);
      }
      return;
    }

    if (isLoggedIn && !savedSearchesQuery.isPending && !savedSearchesQuery.isFetching) {
      clearPersistedSavedSearchKey(savedSearchMatchKey);
      setOptimisticSaved(false);
    }
  }, [
    hasResolvedCurrentUser,
    isLoggedIn,
    matchingSearch,
    savedSearchMatchKey,
    savedSearchesQuery.isFetching,
    savedSearchesQuery.isPending,
  ]);

  const isSaved = (!isPendingRemoval && !!matchingSearch) || optimisticSaved;
  const isWaitingForSavedState =
    (!hasResolvedCurrentUser && optimisticSaved) ||
    (isLoggedIn && savedSearchesQuery.isPending && savedSearchesQuery.data === undefined) ||
    (isLoggedIn && optimisticSaved && !matchingSearch);
  const isDisabled = !hasResolvedCurrentUser || deleteSavedSearch.isPending || isWaitingForSavedState;

  const handleToggle = useCallback(async () => {
    await interactionResponse();

    if (isDisabled) {
      return;
    }

    const searchId = matchingSearchId;
    if (searchId) {
      setPendingRemovalSearchId(searchId);
      deleteSavedSearch.mutate(searchId, {
        onSuccess: () => {
          clearPersistedSavedSearchKey(savedSearchMatchKey);
          setOptimisticSaved(false);
        },
        onError: () => {
          setPendingRemovalSearchId(null);
        },
      });
      return;
    }

    if (!isLoggedIn && optimisticSaved) {
      clearPersistedSavedSearchKey(savedSearchMatchKey);
      setOptimisticSaved(false);
      return;
    }

    openSaveSearchModal();
  }, [
    deleteSavedSearch,
    isDisabled,
    isLoggedIn,
    matchingSearchId,
    openSaveSearchModal,
    optimisticSaved,
    savedSearchMatchKey,
  ]);

  const value = useMemo<SrpSaveSearchContextValue>(
    () => ({
      isSaved,
      isDisabled,
      label: isSaved ? t('savedSearch') : t('saveSearch'),
      handleToggle,
    }),
    [handleToggle, isDisabled, isSaved, t],
  );

  return (
    <SrpSaveSearchContext.Provider value={value}>
      {children}
      <SaveSearchModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSaved={() => {
          emitTrackCreateSearchAlertEvent({
            triggerLocation: 'search_results',
            triggerObject: 'search_results|alerts_modal',
          });
          persistSavedSearchKey(savedSearchMatchKey);
          setOptimisticSaved(true);
        }}
        areaId={areaId}
        resourceId={resourceId}
        searchSlug={searchSlug}
        locationText={locationText}
        areaBounds={areaBounds}
        searchScope={searchScope}
      />
    </SrpSaveSearchContext.Provider>
  );
}

export function useOptionalSrpSaveSearch(): SrpSaveSearchContextValue | null {
  return useContext(SrpSaveSearchContext);
}

export function useSrpSaveSearch(): SrpSaveSearchContextValue {
  const context = useOptionalSrpSaveSearch();

  if (!context) {
    return {
      isSaved: false,
      isDisabled: true,
      label: 'Save search',
      handleToggle: () => {},
    };
  }

  return context;
}
